import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Steps } from 'primereact/steps';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';

// Importar componentes de pasos
import PasoInformacionBasica from './steps/PasoInformacionBasica';
import PasoConfigurarTurnos from './steps/PasoConfigurarTurnos';
import PasoResumen from './steps/PasoResumen';
import { esCorreoValido } from '../CotizacionModificacionRuta/cmrShared';

import './NuevaCotizacion.scss';
import './AnalisisIA.scss'; // diálogo de análisis IA, compartido con el detalle
import { prepararHtmlAnalisis } from './analisisIAHtml';
import {
    createQuote,
    analizarCotizacionIA,
    updateQuote,
    getQuoteById,
    formatHoraForBackend,
    getClientsCatalog,
    getServiceList,
    getShiftsList,
    getVehicleTypesCatalog,
    getTicketsList
} from "./integracion";
import { mapVehiculosParaUI } from "../../../utilidades/vehicleMapper";
import { desEncriptar } from "../../../utilidades/encriptacion";
import {formatearFechaHora, parsearHoraADate} from "../../../utilidades/formateos";

/**
 * Wizard para crear o editar cotizaciones empresariales.
 *
 * @param {Object} props
 * @param {number|string|null} props.cotizacionId - ID de la cotización a editar. Si es null/undefined se crea una nueva.
 * @param {Function} [props.onGuardado] - Callback ejecutado tras guardar exitosamente.
 * @param {Function} [props.onCancelar] - Callback para el botón cancelar (solo en modo edición).
 */
const NuevaCotizacionWizard = ({ cotizacionId = null, onGuardado, onCancelar }) => {
    const toastRef = useRef(null);

    // Determinar modo
    const modoEdicion = Boolean(cotizacionId);

    // Estado del wizard
    const [pasoActivo, setPasoActivo] = useState(0);
    const [guardando, setGuardando] = useState(false);
    const [cargandoCotizacion, setCargandoCotizacion] = useState(false);
    const [analizando, setAnalizando] = useState(false);
    const [analisisIA, setAnalisisIA] = useState(null);

    // Estados del formulario
    const [nombreCotizacion, setNombreCotizacion] = useState('');
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [ticketSeleccionado, setTicketSeleccionado] = useState(null);
    const [tipoUnidad, setTipoUnidad] = useState(null);
    const [turnos, setTurnos] = useState([]);
    const [esFija, setEsFija] = useState(true);
    const [correo, setCorreo] = useState('');
    const [caracteristicasUnidad, setCaracteristicasUnidad] = useState({
        direccionAsistida: false,
        aireAcondicionado: false,
        aforo: false,
        camaraReversa: false,
        sensorFatiga: false,
    });

    // Estados para catálogos
    const [clientes, setClientes] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [tiposVehiculo, setTiposVehiculo] = useState();
    const [vehiculos, setVehiculos] = useState([]);
    const [tiposTurnos, setTiposTurnos] = useState();
    const [tiposServicio, setTiposServicio] = useState();
    const [tipoRuta, setTipoRuta] = useState();
    const [fechaInicial, setFechaInicial] = useState();
    const [fechaFinal, setFechaFinal] = useState();
    const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

    // Obtener sesión del usuario
    const session = JSON.parse(
        desEncriptar(
            sessionStorage.getItem("dataClient"),
            process.env.REACT_APP_CLAVE_INCRIPTACION
        )
    );
    const userId = session?.user?.ID;

    // =========================================================================
    // UTILIDADES DE MAPEO (backend → formulario)
    // =========================================================================



    /**
     * Convierte una fecha string del backend a un objeto Date.
     */
    const parsearFechaADate = (fechaStr) => {
        if (!fechaStr) return null;
        if (fechaStr instanceof Date) return fechaStr;
        const fecha = new Date(fechaStr);
        return isNaN(fecha.getTime()) ? null : fecha;
    };

    /**
     * Mapea los datos de la cotización del backend al estado del formulario.
     * Se ejecuta después de que los catálogos estén cargados.
     */
    const mapearCotizacionAlFormulario = useCallback(async (cotizacion, catalogos) => {
        const {
            clientesCatalogo,
            tiposTurnosCatalogo,
            tiposServicioCatalogo,
        } = catalogos;

        // Nombre
        setNombreCotizacion(cotizacion.nombre || '');

        // Fija
        setEsFija(cotizacion.esFija ?? true);

        // Cliente — buscar por ID en el catálogo
        if (cotizacion.idCliente) {
            const cliente = clientesCatalogo.find(c => c.id === cotizacion.idCliente);
            setClienteSeleccionado(cliente || null);
            if (cotizacion.ticket) {
                try {
                    const ticketsResponse = await getTicketsList(cotizacion.idCliente, 3);
                    if (ticketsResponse.data) {
                        setTickets(ticketsResponse.data);
                        const ticketEncontrado = ticketsResponse.data.find(t => t.id === cotizacion.ticket);
                        setTicketSeleccionado(ticketEncontrado || null);
                    }
                } catch (error) {
                    console.error('Error al cargar tickets para edición:', error);
                }
            }
        }

        if (cotizacion.tipoUnidad) {
            setTipoUnidad(cotizacion.tipoUnidad);
        }

        // Tipo de ruta — buscar en catálogo de servicios
        if (cotizacion.tipoRuta) {
            const tipoRutaObj = tiposServicioCatalogo.find(s => s.id === cotizacion.tipoRuta);
            setTipoRuta(tipoRutaObj || null);
        }


        if (cotizacion.correo) {
            setCorreo(cotizacion.correo);
        }

        if (cotizacion.fechaInicial) {
            setFechaInicial(parsearFechaADate(cotizacion.fechaInicial));
        }

        if (cotizacion.fechaFinal) {
            setFechaFinal(parsearFechaADate(cotizacion.fechaFinal));
        }

        if (cotizacion.caracteristicasUnidad) {
            setCaracteristicasUnidad({
                direccionAsistida: Boolean(cotizacion.caracteristicasUnidad.direccionAsistida),
                aireAcondicionado: Boolean(cotizacion.caracteristicasUnidad.aireAcondicionado),
                aforo: Boolean(cotizacion.caracteristicasUnidad.aforo),
                camaraReversa: Boolean(cotizacion.caracteristicasUnidad.camaraReversa),
                sensorFatiga: Boolean(cotizacion.caracteristicasUnidad.sensorFatiga),
            });
        }

        // Turnos
        if (cotizacion.turnos && Array.isArray(cotizacion.turnos)) {
            const turnosMapeados = cotizacion.turnos.map((turno, index) => {
                const turnoCatalogo = tiposTurnosCatalogo.find(t => t.id === turno.idTurno);
                const servicioCatalogo = tiposServicioCatalogo.find(s => s.id === turno.tipo);

                let diasOperacion = turno.diasOperacion || [];
                if (typeof diasOperacion === 'string') {
                    try {
                        diasOperacion = JSON.parse(diasOperacion);
                    } catch {
                        diasOperacion = diasOperacion.split(',').map(d => d.trim());
                    }
                }

                return {
                    id: turno.id || Date.now() + index,
                    idTurno: turnoCatalogo || null,
                    diasOperacion,
                    horaInicio: parsearHoraADate(turno.horaInicio),
                    horaFin: parsearHoraADate(turno.horaFin),
                    tipoServicio: servicioCatalogo || null,
                };
            });

            setTurnos(turnosMapeados);
        }
    }, []);

    // =========================================================================
    // CARGA DE DATOS
    // =========================================================================

    /**
     * Cargar catálogos desde el backend
     */
    const cargarCatalogos = async () => {
        try {
            setCargandoCatalogos(true);

            const [clientesResponse, vehiculosResponse, turnosTypeResponse, servicesTypeResponse] = await Promise.all([
                getClientsCatalog(),
                getVehicleTypesCatalog(),
                getShiftsList(),
                getServiceList()
            ]);

            const catalogos = {
                clientesCatalogo: [],
                vehiculosCatalogo: [],
                tiposTurnosCatalogo: [],
                tiposServicioCatalogo: [],
            };

            if (clientesResponse.success) {
                setClientes(clientesResponse.data);
                catalogos.clientesCatalogo = clientesResponse.data;
            }

            if (vehiculosResponse.success) {
                const vehiculosMapeados = mapVehiculosParaUI(vehiculosResponse.data);
                catalogos.vehiculosCatalogo = vehiculosResponse.data;
                setVehiculos(vehiculosResponse.data);
                setTiposVehiculo(vehiculosMapeados);
            }

            if (turnosTypeResponse.success) {
                setTiposTurnos(turnosTypeResponse.data);
                catalogos.tiposTurnosCatalogo = turnosTypeResponse.data;
            }

            if (servicesTypeResponse.success) {
                setTiposServicio(servicesTypeResponse.data);
                catalogos.tiposServicioCatalogo = servicesTypeResponse.data;
            }

            return catalogos;
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron cargar los catálogos. Intente recargar la página.',
                life: 5000
            });
            return null;
        } finally {
            setCargandoCatalogos(false);
        }
    };

    /**
     * Cargar cotización existente para edición
     */
    const cargarCotizacion = async (id, catalogos) => {
        try {
            setCargandoCotizacion(true);
            const response = await getQuoteById(id);

            if (response.success && response.data) {
                await mapearCotizacionAlFormulario(response.data, catalogos);  // ← await agregado
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo cargar la cotización. Verifique que exista.',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error al cargar cotización:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar la cotización para edición.',
                life: 5000
            });
        } finally {
            setCargandoCotizacion(false);
        }
    };

    /**
     * Efecto principal: cargar catálogos y, si estamos en modo edición,
     * cargar la cotización una vez que los catálogos estén listos.
     */
    useEffect(() => {
        const inicializar = async () => {
            const catalogos = await cargarCatalogos();

            if (modoEdicion && cotizacionId && catalogos) {
                await cargarCotizacion(cotizacionId, catalogos);
            }
        };

        inicializar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacionId]);


    useEffect(() => {
        if (clienteSeleccionado && !modoEdicion) {
            // Solo en modo creación: cargar tickets al cambiar cliente
            getTicketsList(clienteSeleccionado.id, 3).then((response) => {
                setTickets(response.data);
            });
        }

        if (clienteSeleccionado && modoEdicion && tickets.length > 0) {
            // En modo edición: si cambia el cliente DESPUÉS de la carga inicial
            // (el usuario editó el cliente manualmente), recargar tickets
            // y limpiar la selección porque ya no corresponde
            getTicketsList(clienteSeleccionado.id, 3).then((response) => {
                setTickets(response.data);
                // Si el ticket actual no pertenece al nuevo cliente, limpiarlo
                const ticketExiste = response.data.find(t => t.id === ticketSeleccionado?.id);
                if (!ticketExiste) {
                    setTicketSeleccionado(null);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteSeleccionado]);

    // =========================================================================
    // CONFIGURACIÓN DE PASOS
    // =========================================================================

    const items = [
        {
            label: 'Información Básica',
            command: () => setPasoActivo(0)
        },
        {
            label: 'Configurar Turnos',
            command: () => validarPaso(0) && setPasoActivo(1)
        },
        {
            label: 'Resumen',
            command: () => validarPaso(0) && validarPaso(1) && setPasoActivo(2)
        }
    ];

    // =========================================================================
    // VALIDACIONES
    // =========================================================================

    const validarPaso = (paso) => {
        /*const tieneEntradaYSalida = turnos.some(t => t.idTurno?.entrance === true) &&
            turnos.some(t => t.idTurno?.entrance === false);*/

        switch (paso) {
            case 0:
                if (!nombreCotizacion.trim()) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, ingrese el nombre de la cotización',
                        life: 3000
                    });
                    return false;
                }
                if (!clienteSeleccionado) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione un cliente',
                        life: 3000
                    });
                    return false;
                }
                if (!ticketSeleccionado) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione un ticket',
                        life: 3000
                    });
                    return false;
                }
                if (!tipoUnidad) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione el tipo de unidad para la ruta',
                        life: 3000
                    });
                    return false;
                }
                /*if (!tipoRuta) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione el tipo de ruta',
                        life: 3000
                    });
                    return false;
                }*/
                if (!fechaInicial) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione la fecha inicial',
                        life: 3000
                    });
                    return false;
                }

                if (!esFija && !fechaFinal) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: 'Por favor, seleccione la fecha final',
                        life: 3000
                    });
                    return false;
                }

                if (!esCorreoValido(correo)) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Campo requerido',
                        detail: correo.trim()
                            ? 'Por favor, ingrese un correo de notificación válido'
                            : 'Por favor, ingrese el correo de notificación',
                        life: 3000
                    });
                    return false;
                }

                return true;

            case 1:
                if (turnos.length === 0) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Sin turnos',
                        detail: 'Debe agregar al menos un turno antes de continuar',
                        life: 3000
                    });
                    return false;
                }

                /*if (!tieneEntradaYSalida) {
                    toastRef.current.show({
                        severity: 'warn',
                        summary: 'Turnos incompletos',
                        detail: `Falta agregar un turno de ${turnos.some(t => t.idTurno?.entrance) ? 'Salida' : 'Entrada'} para continuar.`,
                        life: 3000
                    });
                    return false;
                }*/

                return true;

            default:
                return true;
        }
    };

    // =========================================================================
    // ACCIONES
    // =========================================================================

    const limpiarFormulario = () => {
        setNombreCotizacion('');
        setClienteSeleccionado(null);
        setTicketSeleccionado(null);
        setTipoUnidad(null);
        setTipoRuta(null);
        setTurnos([]);
        setFechaInicial(undefined);
        setFechaFinal(undefined);
        setCaracteristicasUnidad({
            direccionAsistida: false,
            aireAcondicionado: false,
            aforo: false,
            camaraReversa: false,
            sensorFatiga: false,
        });
        setPasoActivo(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const siguientePaso = () => {
        if (validarPaso(pasoActivo)) {
            setPasoActivo(prev => Math.min(prev + 1, items.length - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const pasoAnterior = () => {
        setPasoActivo(prev => Math.max(prev - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /**
     * Construir el payload de la cotización para enviar al backend.
     * Se reutiliza tanto para crear como para actualizar.
     */
    const construirPayload = () => {
        const fechaFinalPayload = esFija
            ? null
            : formatearFechaHora(fechaFinal);

        return {
            nombre: nombreCotizacion?.trim().toUpperCase(),
            idCliente: clienteSeleccionado?.id,
            ticket: ticketSeleccionado?.id,
            usuario: userId,
            tipoUnidad: tipoUnidad,
            correo,
            tipoRuta: tipoRuta?.id,
            esFija,
            caracteristicasUnidad,
            turnos: turnos.map(turno => ({
                ...(turno.id && modoEdicion ? { id: turno.id } : {}),
                idTurno: turno.idTurno.id,
                diasOperacion: turno.diasOperacion || [],
                horaInicio: formatHoraForBackend(turno.horaInicio),
                horaFin: formatHoraForBackend(turno.horaFin),
                tipo: turno.tipoServicio.id,
                fechaInicial: formatearFechaHora(fechaInicial),
                fechaFinal: fechaFinalPayload,
            }))
        };
    };

    /**
     * Datos de la cotización con nombres en lugar de IDs, para que la IA los entienda.
     */
    const construirDatosAnalisis = () => ({
        nombre: nombreCotizacion?.trim().toUpperCase(),
        cliente: clienteSeleccionado?.nombre,
        ticket: ticketSeleccionado?.nombre,
        correo,
        tipoUnidad: vehiculos.find(v => v.ID === tipoUnidad)?.TIPO ?? tipoUnidad,
        tipoRuta: tipoRuta?.nombre ?? null,
        esFija,
        fechaInicial: formatearFechaHora(fechaInicial),
        fechaFinal: esFija ? null : formatearFechaHora(fechaFinal),
        caracteristicasUnidad,
        turnos: turnos.map(turno => ({
            turno: turno.idTurno?.nombre,
            tipoServicio: turno.tipoServicio?.nombre,
            diasOperacion: turno.diasOperacion || [],
            horaInicio: formatHoraForBackend(turno.horaInicio),
            horaFin: formatHoraForBackend(turno.horaFin),
        })),
    });

    /**
     * Antes de crear una cotización nueva la IA revisa los datos.
     * - Sin cambios → se guarda directo.
     * - Con cambios (o respuesta en formato inesperado) → se le muestran a la inplant para que decida.
     * - Si la IA falla → se guarda igual, el análisis no debe bloquear el proceso.
     */
    const handleGuardarCotizacion = async () => {
        if (!validarPaso(0) || !validarPaso(1)) return;

        if (modoEdicion) {
            await guardarCotizacion();
            return;
        }

        const datos = construirDatosAnalisis();

        let analisis = null;
        try {
            setAnalizando(true);
            analisis = await analizarCotizacionIA(datos, userId);
        } catch (e) {
            console.error('Error en el análisis IA de la cotización:', e);
        } finally {
            setAnalizando(false);
        }

        const analisisGuardar = { jsonAnalisisIA: analisis?.json ?? null, htmlAnalisisIA: analisis?.html ?? null };

        // Siempre se muestra el análisis; se guarda hasta que le den "Continuar"
        if (analisis?.html) {
            setAnalisisIA({ ...analisis, analisisGuardar });
            return;
        }

        await guardarCotizacion(analisisGuardar);
    };

    // Solo es "Todo en orden" si la IA lo confirma; null = respondió en formato inesperado y hay que leer el reporte
    const cambiosIA = analisisIA?.cambios ?? [];
    const hayErroresIA = analisisIA?.requiereCambios !== false || cambiosIA.length > 0;

    const handleContinuar = async () => {
        const { analisisGuardar } = analisisIA;
        setAnalisisIA(null);
        await guardarCotizacion(analisisGuardar);
    };

    /**
     * Guarda/actualiza la cotización en el backend.
     * @param {{jsonAnalisisIA: string|null, htmlAnalisisIA: string|null}} [analisisGuardar] - JSON y HTML que respondió la IA (solo al crear)
     */
    const guardarCotizacion = async (analisisGuardar = {}) => {
        try {
            setGuardando(true);

            const payload = { ...construirPayload(), ...analisisGuardar };

            let idGenerado;

            if (modoEdicion) {
                await updateQuote(cotizacionId, payload);
                toastRef.current.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'La cotización se ha actualizado correctamente',
                    life: 3000
                });

                // Ejecutar callback si existe
                if (onGuardado) {
                    onGuardado();
                }
            } else {
                idGenerado = await createQuote(payload);

                toastRef.current.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'La cotización se ha creado correctamente',
                    life: 3000
                });

                limpiarFormulario();
            }

            if (onGuardado) {
                onGuardado(idGenerado.idCotizacion);
            }

        } catch (e) {
            console.error('Error al guardar cotización:', e);
            toastRef.current.show({
                severity: 'error',
                summary: '¡Error!',
                detail: modoEdicion
                    ? 'No se pudo actualizar la cotización. Intente más tarde.'
                    : 'Se ha producido un error, intente más tarde',
                life: 3000
            });
        } finally {
            setGuardando(false);
        }
    };

    // =========================================================================
    // RENDER
    // =========================================================================

    /**
     * Pantalla de carga mientras se obtiene la cotización
     */
    if (cargandoCotizacion || (modoEdicion && cargandoCatalogos)) {
        return (
            <div className="wizard-container">
                <Toast ref={toastRef} position="top-right" />
                <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                    <ProgressSpinner
                        style={{ width: '60px', height: '60px' }}
                        strokeWidth="4"
                    />
                    <p className="mt-3 text-600 font-semibold">
                        {cargandoCatalogos ? 'Cargando catálogos...' : 'Cargando cotización...'}
                    </p>
                </div>
            </div>
        );
    }

    const renderPasoContenido = () => {
        switch (pasoActivo) {
            case 0:
                return (
                    <PasoInformacionBasica
                        nombreCotizacion={nombreCotizacion}
                        setNombreCotizacion={setNombreCotizacion}
                        clienteSeleccionado={clienteSeleccionado}
                        tickets={tickets}
                        ticketSeleccionado={ticketSeleccionado}
                        setClienteSeleccionado={setClienteSeleccionado}
                        setTicketSeleccionado={setTicketSeleccionado}
                        clientes={clientes}
                        correo={correo}
                        setCorreo={setCorreo}
                        fechaInicial={fechaInicial}
                        setFechaInicial={setFechaInicial}
                        fechaFinal={fechaFinal}
                        setFechaFinal={setFechaFinal}
                        tipoUnidad={tipoUnidad}
                        setTipoUnidad={setTipoUnidad}
                        tiposVehiculo={tiposVehiculo}
                        tipoRuta={tipoRuta}
                        setTipoRuta={setTipoRuta}
                        tiposServicio={tiposServicio}
                        esFija={esFija}
                        setEsFija={setEsFija}
                        caracteristicasUnidad={caracteristicasUnidad}
                        setCaracteristicasUnidad={setCaracteristicasUnidad}
                        modoEdicion={modoEdicion}
                    />
                );

            case 1:
                return (
                    <PasoConfigurarTurnos
                        turnos={turnos}
                        setTurnos={setTurnos}
                        toastRef={toastRef}
                        nombresTurnos={tiposTurnos}
                        tiposServicio={tiposServicio}
                    />
                );

            case 2:
                return (
                    <PasoResumen
                        nombreCotizacion={nombreCotizacion}
                        clienteSeleccionado={clienteSeleccionado}
                        turnos={turnos}
                        onEditar={setPasoActivo}
                        tipoUnidad={tipoUnidad}
                        tiposVehiculo={vehiculos}
                        modoEdicion={modoEdicion}
                    />
                );

            default:
                return null;
        }
    };

    const renderBotonesNavegacion = () => {
        const esUltimoPaso = pasoActivo === items.length - 1;
        const esPrimerPaso = pasoActivo === 0;

        return (
            <div className="wizard-navegacion">
                <div className="flex gap-3 align-items-center">
                    {/* Botón cancelar en modo edición */}
                    {modoEdicion && esPrimerPaso && onCancelar && (
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            onClick={onCancelar}
                            className="p-button-outlined p-button-secondary"
                            disabled={guardando}
                        />
                    )}

                    {!esPrimerPaso && (
                        <Button
                            label="Anterior"
                            icon="pi pi-arrow-left"
                            onClick={pasoAnterior}
                            className="p-button-outlined"
                            disabled={guardando}
                        />
                    )}

                    {!esUltimoPaso ? (
                        <Button
                            label="Siguiente"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            onClick={siguientePaso}
                            className="p-button-primary"
                        />
                    ) : (
                        <Button
                            label={
                                analizando
                                    ? 'Analizando...'
                                    : guardando
                                        ? 'Guardando...'
                                        : modoEdicion
                                            ? 'Actualizar Cotización'
                                            : 'Crear Cotización'
                            }
                            icon={guardando || analizando ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                            onClick={handleGuardarCotizacion}
                            disabled={guardando || analizando}
                            className="p-button-success"
                        />
                    )}
                </div>

                <div className="paso-indicador">
                    <span>Paso {pasoActivo + 1} de {items.length}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="wizard-container">
            <Toast ref={toastRef} position="top-right" />

            {/* Banner informativo en modo edición */}
            {modoEdicion && (
                <Message
                    severity="warn"
                    className="mb-3 w-full edicion-banner"
                    content={
                        <div className="flex align-items-center gap-3">
                            <i className="pi pi-pencil" style={{ fontSize: '1.3rem' }}></i>
                            <div>
                                <strong>Modo edición</strong>
                                <p className="m-0 mt-1" style={{ fontSize: '0.9rem' }}>
                                    Está editando la cotización <strong>"{nombreCotizacion}"</strong>.
                                    Los cambios se guardarán al hacer clic en "Actualizar Cotización".
                                </p>
                            </div>
                        </div>
                    }
                />
            )}

            <Card className="wizard-steps-card">
                <Steps
                    model={items}
                    activeIndex={pasoActivo}
                    readOnly={false}
                    className="wizard-steps"
                />
            </Card>

            <div className="wizard-content">
                {renderPasoContenido()}
            </div>

            {renderBotonesNavegacion()}

            <Dialog
                className={`analisis-ia-dialog ${hayErroresIA ? 'con-errores' : 'sin-errores'}`}
                header={
                    <span className="analisis-ia-titulo">
                        <i className="pi pi-sparkles" />
                        Análisis de la cotización
                    </span>
                }
                visible={Boolean(analisisIA)}
                onHide={() => setAnalisisIA(null)}
                style={{ width: '60rem' }}
                breakpoints={{ '960px': '95vw' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Corregir"
                            icon="pi pi-pencil"
                            className="p-button-outlined"
                            onClick={() => setAnalisisIA(null)}
                        />
                        <Button
                            label={hayErroresIA ? 'Guardar con errores' : 'Guardar'}
                            icon={hayErroresIA ? 'pi pi-exclamation-triangle' : 'pi pi-check'}
                            className={hayErroresIA ? 'p-button-danger' : 'p-button-success'}
                            loading={guardando}
                            onClick={handleContinuar}
                        />
                    </div>
                }
            >
                <div className="analisis-ia-estado">
                    <div className="analisis-ia-estado-icono">
                        <i className={hayErroresIA ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'} />
                    </div>
                    <div>
                        <div className="analisis-ia-estado-titulo">
                            {hayErroresIA
                                ? cambiosIA.length > 0
                                    ? `Se encontraron ${cambiosIA.length} punto${cambiosIA.length === 1 ? '' : 's'} por revisar`
                                    : analisisIA?.requiereCambios === null
                                        ? 'Revisa el reporte de la IA'
                                        : 'Se encontraron puntos por revisar'
                                : 'Todo en orden'}
                        </div>
                        <div className="analisis-ia-estado-texto">
                            {hayErroresIA
                                ? 'Revisa los detalles antes de guardar. Puedes corregir o guardar la cotización con errores.'
                                : 'La IA no encontró nada que cambiar en la cotización.'}
                        </div>
                    </div>
                </div>

                {/* La IA responde el reporte ya en HTML (HTML_ANALISIS_IA); el diseño lo pone .analisis-ia-html */}
                <div className="analisis-ia-html" dangerouslySetInnerHTML={{ __html: prepararHtmlAnalisis(analisisIA?.html) }} />
            </Dialog>
        </div>
    );
};

export default NuevaCotizacionWizard;
